import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, ArrowDown, Pause, Play, X, Check, Gift } from 'lucide-react';

const SubscriptionManager = () => {
  const navigate = useNavigate();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load current subscription
      const dashboardResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (dashboardResponse.ok) {
        const dashboardData = await dashboardResponse.json();
        setCurrentSubscription(dashboardData.current_subscription);
      }
      
      // Load available plans
      const plansResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (plansResponse.ok) {
        const plansData = await plansResponse.json();
        setAvailablePlans(plansData.packages || []);
      }
      
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async () => {
    if (!selectedPlan) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/subscription/change`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_tier: selectedPlan.tier,
          billing_cycle: selectedPlan.billing_cycle || 'monthly',
          promo_code: promoCode || null,
          immediate: true
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Subscription changed successfully! ${result.proration_amount > 0 ? `Proration: $${result.proration_amount.toFixed(2)}` : ''}`);
        setShowChangeModal(false);
        loadSubscriptionData();
      } else {
        const error = await response.json();
        alert(`Failed to change subscription: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error changing subscription:', error);
      alert('An error occurred while changing your subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handlePauseSubscription = async () => {
    if (!window.confirm('Are you sure you want to pause your subscription?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/subscription/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('Subscription paused successfully');
        loadSubscriptionData();
      } else {
        alert('Failed to pause subscription');
      }
    } catch (error) {
      console.error('Error pausing subscription:', error);
      alert('An error occurred while pausing your subscription');
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoValidation(null);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/promo-code/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ promo_code: promoCode })
      });
      
      if (response.ok) {
        const result = await response.json();
        setPromoValidation({
          valid: true,
          message: result.message,
          discount_type: result.discount_type,
          discount_value: result.discount_value
        });
      } else {
        setPromoValidation({
          valid: false,
          message: 'Invalid or expired promo code'
        });
      }
    } catch (error) {
      setPromoValidation({
        valid: false,
        message: 'Failed to validate promo code'
      });
    }
  };

  const getPlanFeatures = (tier) => {
    const features = {
      free: [
        '3 speed dating sessions per month',
        'Icebreakers included in sessions',
        'Basic matching'
      ],
      premium: [
        'Unlimited speed dating sessions',
        'Unlimited AI icebreakers',
        'Advanced matching algorithm',
        'Chat with matches',
        'Video calling features'
      ],
      vip: [
        'All Premium features',
        'Personal coaching dashboard',
        'Compatibility reports',
        'Dating analytics & insights',
        'Unlimited messaging'
      ]
    };
    return features[tier] || [];
  };

  const getPlanPrice = (tier, cycle = 'monthly') => {
    const prices = {
      free: { monthly: 0, yearly: 0 },
      premium: { monthly: 9.99, yearly: 99.99 },
      vip: { monthly: 19.99, yearly: 199.99 }
    };
    return prices[tier]?.[cycle] || 0;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Subscription Management</h1>
          <p className="text-white/80">Manage your subscription and explore upgrade options</p>
        </div>

        {/* Current Subscription Card */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8">
          <h2 className="text-2xl font-semibold text-white mb-4">Current Subscription</h2>
          
          {currentSubscription ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-4 h-4 rounded-full ${
                    currentSubscription.status === 'active' ? 'bg-green-400' : 
                    currentSubscription.status === 'paused' ? 'bg-yellow-400' : 'bg-red-400'
                  }`}></div>
                  <span className="text-white text-2xl font-bold capitalize">
                    {currentSubscription.tier} Plan
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    currentSubscription.status === 'active' ? 'bg-green-500/20 text-green-300' :
                    currentSubscription.status === 'paused' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-red-500/20 text-red-300'
                  }`}>
                    {currentSubscription.status}
                  </span>
                </div>
                
                <div className="space-y-2 text-white/80">
                  <p>Started: {new Date(currentSubscription.start_date).toLocaleDateString()}</p>
                  {currentSubscription.end_date && (
                    <p>
                      {currentSubscription.auto_renew ? 'Renews' : 'Expires'}: {' '}
                      {new Date(currentSubscription.end_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => navigate('/subscription/plans')}
                  className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-full flex items-center justify-center space-x-2 transition-colors"
                >
                  <ArrowUp className="h-4 w-4" />
                  <span>Change Plan</span>
                </button>
                
                {currentSubscription.status === 'active' && (
                  <button
                    onClick={handlePauseSubscription}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-full flex items-center justify-center space-x-2 transition-colors"
                  >
                    <Pause className="h-4 w-4" />
                    <span>Pause Subscription</span>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-white/80 mb-4">You're currently on the free plan</p>
              <button
                onClick={() => navigate('/subscription/plans')}
                className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-3 rounded-full text-lg font-semibold transition-colors"
              >
                Upgrade Now
              </button>
            </div>
          )}
        </div>

        {/* Plan Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {['free', 'premium', 'vip'].map((tier) => (
            <div key={tier} className={`bg-white/10 backdrop-blur-md rounded-2xl p-6 ${
              currentSubscription?.tier === tier ? 'ring-2 ring-white' : ''
            }`}>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white capitalize mb-2">{tier}</h3>
                <div className="text-3xl font-bold text-white">
                  {formatCurrency(getPlanPrice(tier, 'monthly'))}
                  <span className="text-sm text-white/60">/month</span>
                </div>
                {tier !== 'free' && (
                  <p className="text-white/60 text-sm mt-1">
                    {formatCurrency(getPlanPrice(tier, 'yearly'))}/year (save 17%)
                  </p>
                )}
              </div>
              
              <ul className="space-y-3 mb-6">
                {getPlanFeatures(tier).map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2 text-white/80">
                    <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              {currentSubscription?.tier !== tier && (
                <button
                  onClick={() => {
                    setSelectedPlan({ tier, billing_cycle: 'monthly' });
                    setShowChangeModal(true);
                  }}
                  className={`w-full py-2 px-4 rounded-full font-semibold transition-colors ${
                    tier === 'free' 
                      ? 'bg-gray-500 hover:bg-gray-600 text-white'
                      : tier === 'premium'
                      ? 'bg-rose-500 hover:bg-rose-600 text-white'
                      : 'bg-purple-500 hover:bg-purple-600 text-white'
                  }`}
                >
                  {tier === 'free' ? 'Downgrade' : 'Upgrade'}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Change Plan Modal */}
        {showChangeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Change Subscription</h3>
                <button
                  onClick={() => setShowChangeModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {selectedPlan && (
                <div className="mb-6">
                  <p className="text-gray-600 mb-2">Selected Plan:</p>
                  <div className="bg-gray-100 rounded-lg p-3">
                    <p className="font-semibold capitalize">{selectedPlan.tier} Plan</p>
                    <p className="text-sm text-gray-600">
                      {formatCurrency(getPlanPrice(selectedPlan.tier, selectedPlan.billing_cycle))}
                      /{selectedPlan.billing_cycle === 'yearly' ? 'year' : 'month'}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Promo Code Section */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Promo Code (Optional)
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter promo code"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                  />
                  <button
                    onClick={validatePromoCode}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <Gift className="h-4 w-4" />
                  </button>
                </div>
                
                {promoValidation && (
                  <div className={`mt-2 p-2 rounded text-sm ${
                    promoValidation.valid 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {promoValidation.message}
                  </div>
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowChangeModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePlanChange}
                  disabled={processing || !selectedPlan}
                  className="flex-1 bg-rose-500 hover:bg-rose-600 disabled:bg-rose-300 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  {processing ? 'Processing...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManager;